// frontend/src/components/BannerSlider.jsx
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import bannerService from "../services/bannerService";
import { Loader2 } from "lucide-react";

import "swiper/css";
import "swiper/css/pagination";

export default function BannerSlider() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const swiperRef = useRef(null);
  const progressInterval = useRef(null);
  const isPausedRef = useRef(false);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    setLoading(true);
    const result = await bannerService.getActiveBanners();
    if (result.success) {
      setBanners(result.data);
    }
    setLoading(false);
  };

  const getImageUrl = (banner) => {
    if (!banner) return "/placeholder-image.jpg";

    let image = isMobile ? banner.imagem_mobile : banner.imagem_desktop;
    if (!image) image = banner.imagem_desktop;
    if (!image) return "/placeholder-image.jpg";

    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    let baseUrl = "";
    if (process.env.NODE_ENV === "development") {
      baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    } else {
      baseUrl = "https://brechovizinho.com.br";
    }

    let path = image.startsWith("/") ? image : "/" + image;
    if (!path.startsWith("/storage/")) {
      path = "/storage" + path;
    }

    return `${baseUrl}${path}`;
  };

  const handleSlideChange = (swiper) => {
    setActiveIndex(swiper.realIndex);
    setProgress(0);
    clearInterval(progressInterval.current);
    setTimeout(() => {
      startProgressBar();
    }, 100);
  };

  const handleMouseEnter = () => {
    isPausedRef.current = true;
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }
  };

  const handleMouseLeave = () => {
    isPausedRef.current = false;
    if (!isPausedRef.current && banners.length > 0) {
      startProgressBar();
    }
  };

  const startProgressBar = () => {
    if (banners.length === 0) return;
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }

    // 🔥 CAMBIADO: 5000 → 7000 (7 segundos)
    const duration = 7000;
    const interval = 30;
    const step = (interval / duration) * 100;

    progressInterval.current = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + step;
        if (newProgress >= 100) {
          clearInterval(progressInterval.current);
          if (swiperRef.current && !isPausedRef.current) {
            swiperRef.current.slideNext();
          }
          return 100;
        }
        return newProgress;
      });
    }, interval);
  };

  useEffect(() => {
    if (banners.length === 0) return;

    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }

    setProgress(0);
    const timer = setTimeout(() => {
      startProgressBar();
    }, 200);

    return () => {
      clearTimeout(timer);
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [activeIndex, banners.length]);

  if (loading) {
    return (
      <div
        className={`bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex items-center justify-center ${
          isMobile ? "aspect-[3/2]" : "aspect-[5.25/1]"
        }`}
      >
        <Loader2 className="w-8 h-8 text-[#449879] animate-spin" />
      </div>
    );
  }

  if (banners.length === 0) {
    return (
      <div
        className={`bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl overflow-hidden shadow-sm flex items-center justify-center ${
          isMobile ? "aspect-[3/2]" : "aspect-[5.25/1]"
        }`}
      >
        <div className="text-center">
          <p className="text-gray-400 text-sm">Nenhum banner disponível</p>
        </div>
      </div>
    );
  }

  const showArrows = banners.length > 1;

  return (
    <div
      className="relative w-full"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative rounded-2xl overflow-visible shadow-lg w-full">
        <Swiper
          modules={[Autoplay]}
          spaceBetween={0}
          slidesPerView={1}
          speed={800}
          // 🔥 CAMBIADO: 5000 → 7000 (7 segundos)
          autoplay={{
            delay: 7000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          loop={showArrows}
          className="rounded-2xl overflow-hidden w-full"
          onSlideChange={handleSlideChange}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
        >
          {banners.map((banner) => {
            const imageUrl = getImageUrl(banner);
            const content = (
              <div
                className={`relative w-full bg-white ${
                  isMobile ? "aspect-[4/4]" : "aspect-[5.25/1]"
                }`}
              >
                <img
                  src={imageUrl}
                  alt={banner.titulo}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.error("Error loading banner image:", imageUrl);
                    e.target.src = "/placeholder-image.jpg";
                  }}
                />
              </div>
            );

            if (banner.link) {
              return (
                <SwiperSlide key={banner.id}>
                  <Link to={banner.link} className="block w-full h-full">
                    {content}
                  </Link>
                </SwiperSlide>
              );
            }

            return <SwiperSlide key={banner.id}>{content}</SwiperSlide>;
          })}
        </Swiper>

        {showArrows && !isMobile && (
          <>
            <button
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-8 h-8 lg:w-9 lg:h-9 bg-white hover:bg-[#449879] rounded-full shadow-lg items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-xl group"
              aria-label="Anterior"
              onClick={() => {
                if (swiperRef.current) {
                  swiperRef.current.slidePrev();
                }
              }}
            >
              <svg
                className="w-4 h-4 lg:w-4.5 lg:h-4.5 text-[#449879] group-hover:text-white transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 w-8 h-8 lg:w-9 lg:h-9 bg-white hover:bg-[#449879] rounded-full shadow-lg items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-xl group"
              aria-label="Próxima"
              onClick={() => {
                if (swiperRef.current) {
                  swiperRef.current.slideNext();
                }
              }}
            >
              <svg
                className="w-4 h-4 lg:w-4.5 lg:h-4.5 text-[#449879] group-hover:text-white transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mt-4 flex justify-center gap-1.5 px-4">
        {banners.map((_, index) => (
          <div
            key={index}
            className={`relative h-1.5 bg-gray-200 rounded-full overflow-hidden cursor-pointer transition-all duration-300 ${
              isMobile ? "w-12" : "w-16"
            }`}
            onClick={() => {
              if (swiperRef.current) {
                swiperRef.current.slideTo(index);
              }
            }}
          >
            <div
              className={`h-full rounded-full transition-all duration-300 ease-linear ${
                index === activeIndex ? "bg-[#449879]" : "bg-gray-300"
              }`}
              style={{
                width: index === activeIndex ? `${progress}%` : "0%",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}